'use client'

import React from 'react'
import { CustomButton, CustomLoadingButton } from './CustomButtons'
import { ButtonProps as MuiButtonProps } from '@mui/material'

/************************************************************************
  #### 이 름 : `ButtonConfig`
  #### 설  명 : 버튼에 설정할 값의 타입을 정의한 인터페이스  
  #### 프로퍼티 :  

> - name(string): element를 식별할 수 있는 HTML의 기본 속성
> - actionType(string): 사용할 버튼 양식을 구분하는 문자열. `buttonComponents`의 각 프로퍼티명과 일치해야 함  
> - customText(string): 버튼에 표시할 텍스트
> - isVisible(boolean): 버튼의 표시 여부
> - isSubmitting(boolean): 'submit' 이벤트 발생 시 버튼의 비활성화 여부
> - type(string): 버튼 element의 기능을 지정하는 HTML의 기본 속성
> - handleBtnClick(funtion): 개별 버튼의 독립적인 클릭 이벤트 핸들러(있으면 공통 이벤트 핸들러를 무시하고 사용함)
 ************************************************************************/
export interface ButtonConfig extends MuiButtonProps {
  name: string
  actionType?:
    | 'submit'
    | 'search'
    | 'recall'
    | 'update'
    | 'fileDeleteCancle'
    | 'delete'
    | 'fileDelete'
    | 'excel'
    | 'save'
    | 'cancel'
    | 'reset'
    | 'popup'
    | 'close'
  customText?: string
  isVisible?: boolean
  isSubmitting?: boolean
  type?: 'button' | 'submit' | 'reset'
  handleBtnClick?: (...args: any[]) => any
}

/************************************************************************
  #### 이 름 : `CommonButtonGroupProps`
  #### 설  명 : 버튼 그룹에 설정할 값의 타입을 정의한 인터페이스  
  #### 프로퍼티 :  

  > - buttons(ButtonConfig[]): 각 버튼에 넣을 props들의 배열
 ************************************************************************/
interface CommonButtonGroupProps {
  buttons: ButtonConfig[]
}

/************************************************************************
  ## 알림: 해당 컴포넌트는 이 파일 안에서 사용하는 용도로 변경됨 --> 양식 이외의 버튼이 필요한 경우 `CustomBtnGroup` 사용
  #### 이 름 : `CommonButtonGroup`
  #### 설  명 : MUI 버튼 컴포넌트에 동적으로 props를 넣기 위한 컴포넌트 
  #### 인  자 :  

  > - buttons(ButtonConfig[]): 각 버튼에 넣을 props들의 배열

  #### 사용법 :  
  
  ```tsx
  const defaultButtons: ButtonConfig[] = [
        {
          name: 'search',
          actionType: 'search',
          disabled: isSubmitting,
          handleBtnClick: handleBtnClick || searchProps.handleBtnClick,
          ...searchProps,
        },
        {
          name: 'reset',
          actionType: 'reset',
          disabled: isSubmitting,
          handleBtnClick: handleBtnClick || searchProps.handleBtnClick,
          ...resetProps,
        },
      ]

  const allButtons = [...buttons, ...defaultButtons]

  return <CommonButtonGroup buttons={allButtons} />
  ```
 ************************************************************************/
export const CommonButtonGroup = ({ buttons }: CommonButtonGroupProps) => {
  const buttonComponents: { [key: string]: React.FC<ButtonConfig> } = {
    submit: (props) => (
      <CustomButton
        type={props.type || 'button'}
        variant={props.variant || 'contained'}
        color={props.color || 'primary'}
        disabled={props.disabled}
        onClick={props.handleBtnClick}
        {...props}
      >
        {props.customText || '제출'}
      </CustomButton>
    ),
    search: (props) => (
      <CustomLoadingButton
        type={props.type || 'submit'}
        variant={props.variant || 'contained'}
        color={props.color || 'primary'}
        loading={props.disabled}
        onClick={props.handleBtnClick}
        {...props}
      >
        {props.customText || '검색'}
      </CustomLoadingButton>
    ),
    recall: (props) => (
      <CustomButton
        type={props.type || 'button'}
        variant={props.variant || 'contained'}
        color={props.color || 'primary'}
        disabled={props.disabled}
        onClick={props.handleBtnClick}
        {...props}
      >
        {props.customText || '회수'}
      </CustomButton>
    ),
    update: (props) => (
      <CustomButton
        type={props.type || 'button'}
        variant={props.variant || 'contained'}
        color={props.color || 'primary'}
        disabled={props.disabled}
        onClick={props.handleBtnClick}
        {...props}
      >
        {props.customText || '수정'}
      </CustomButton>
    ),
    fileDeleteCancle: (props) => (
      <CustomButton
        type={props.type || 'button'}
        variant={props.variant || 'contained'}
        color={props.color || 'secondary'}
        disabled={props.disabled}
        onClick={props.handleBtnClick}
        {...props}
      >
        {props.customText || '파일삭제취소'}
      </CustomButton>
    ),
    delete: (props) => (
      <CustomButton
        type={props.type || 'button'}
        variant={props.variant || 'contained'}
        color={props.color || 'error'}
        disabled={props.disabled}
        onClick={props.handleBtnClick}
        {...props}
      >
        {props.customText || '삭제'}
      </CustomButton>
    ),
    fileDelete: (props) => (
      <CustomButton
        type={props.type || 'button'}
        variant={props.variant || 'contained'}
        color={props.color || 'error'}
        disabled={props.disabled}
        onClick={props.handleBtnClick}
        {...props}
      >
        {props.customText || '파일삭제'}
      </CustomButton>
    ),
    excel: (props) => (
      <CustomButton
        type={props.type || 'button'}
        variant={props.variant || 'contained'}
        color={props.color || 'success'}
        disabled={props.disabled}
        onClick={props.handleBtnClick}
        {...props}
      >
        {props.customText || '엑셀'}
      </CustomButton>
    ),
    save: (props) => (
      <CustomButton
        type={props.type || 'button'}
        variant={props.variant || 'contained'}
        color={props.color || 'dark'}
        disabled={props.disabled}
        onClick={props.handleBtnClick}
        {...props}
      >
        {props.customText || '임시저장'}
      </CustomButton>
    ),
    cancel: (props) => (
      <CustomButton
        type={props.type || 'button'}
        variant={props.variant || 'contained'}
        color={props.color || 'dark'}
        disabled={props.disabled}
        onClick={props.handleBtnClick}
        {...props}
      >
        {props.customText || '취소'}
      </CustomButton>
    ),
    reset: (props) => (
      <CustomButton
        type={props.type || 'button'}
        variant={props.variant || 'contained'}
        color={props.color || 'dark'}
        disabled={props.disabled}
        onClick={props.handleBtnClick}
        {...props}
      >
        {props.customText || '초기화'}
      </CustomButton>
    ),
    popup: (props) => (
      <CustomButton
        type={props.type || 'button'}
        variant={props.variant || 'contained'}
        color={props.color || 'dark'}
        disabled={props.disabled}
        onClick={props.handleBtnClick}
        {...props}
      >
        {props.customText || '팝업'}
      </CustomButton>
    ),
    close: (props) => (
      <CustomButton
        type={props.type || 'button'}
        variant={props.variant || 'contained'}
        color={props.color || 'dark'}
        disabled={props.disabled}
        onClick={props.handleBtnClick}
        {...props}
      >
        {props.customText || '닫기'}
      </CustomButton>
    ),
  }

  return (
    <>
      {buttons.map((button) => {
        const {
          actionType,
          isVisible,
          isSubmitting,
          handleBtnClick,
          ...otherProps
        } = button // actionType, handleBtnClick 분리

        const ButtonComponent =
          (actionType && buttonComponents[actionType]) || CustomButton
        return isVisible !== false ? (
          <ButtonComponent
            key={button.name}
            disabled={isSubmitting}
            onClick={handleBtnClick}
            {...otherProps}
          />
        ) : null
      })}
    </>
  )
}

/************************************************************************
  #### 이 름 : `BaseBtnGroupProps`
  #### 설  명 : 기본 버튼 그룹 Props를 정의한 인터페이스  
  #### 프로퍼티 :  

  > - isSubmitting(boolean): 버튼의 비활성화 여부
  > - handleBtnClick(function): 버튼 클릭 이벤트 핸들러
 ************************************************************************/
interface BaseBtnGroupProps {
  isSubmitting?: boolean
  handleBtnClick?: (event: React.MouseEvent<HTMLButtonElement>) => void
  buttons?: ButtonConfig[]
}

/************************************************************************
#### 이 름 : `CustomBtnGroupProps`
#### 설 명 : 커스텀 버튼 그룹 Props를 정의한 인터페이스
#### 프로퍼티 :

> - isSubmitting(boolean): 버튼의 비활성화 여부
> - handleBtnClick(function): 공통 버튼 클릭 이벤트 핸들러
> - buttons(ButtonConfig[]): 커스텀 버튼 설정 배열
************************************************************************/
interface CustomBtnGroupProps extends BaseBtnGroupProps {
  buttons: ButtonConfig[]
}

/************************************************************************
#### 이 름 : `CustomBtnGroup`
#### 설 명 : 완전히 커스터마이즈 가능한 버튼 그룹 컴포넌트
#### 인 자 :

> - isSubmitting(boolean): 버튼의 비활성화 여부
> - handleBtnClick(function): 공통 버튼 클릭 이벤트 핸들러
> - buttons(ButtonConfig[]): 커스텀 버튼 설정 배열
>   - **name(required, string)**: element를 식별할 수 있는 HTML의 기본 속성
>   - **actionType(required, string)**: 사용할 버튼 양식을 구분하는 문자열. `buttonComponents`의 각 프로퍼티명과 일치해야 함  
>   - customText(string): 버튼에 표시할 텍스트
>   - isVisible(boolean): 버튼의 표시 여부(Default: true)
>   - isSubmitting(boolean): 'submit' 이벤트 발생 시 버튼의 비활성화 여부
>   - type(string): 버튼 element의 기능을 지정하는 HTML의 기본 속성(Default: 각 actionType마다 다름)
>   - handleBtnClick(funtion): 개별 버튼 클릭 이벤트 핸들러(있으면 공통 이벤트 핸들러를 무시하고 사용함)
>   - variant(string): MUI 버튼의 외관과 강조 정도를 지정('text' | 'contained' | 'outlined')
>   - color(string): MUI 버튼의 색상 테마를 지정('primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' | 'inherit')

#### 사용법 :

버튼의 순서는 배열의 인덱스와 일치함(왼쪽부터 오른쪽으로)

```tsx
<CustomBtnGroup
      isSubmitting={loading}
      buttons={[
        {
          name: 'custom2',
          actionType: 'excel',
          customText: '커스텀2',
          handleBtnClick: () => {
            alert('커스텀2')
          },
          isVisible: false,
        },
        {
          name: 'custom1',
          actionType: 'cancel',
          customText: '커스텀1',
          handleBtnClick: () => {
            alert('커스텀1')
          },
        },
        {
          name: 'custom3',
          type: 'submit',
          actionType: 'cancel',
          customText: '커스텀3',
          variant: 'outlined',
          color: 'warning',
        },
      ]}
/>
```
************************************************************************/
export const CustomBtnGroup = ({
  isSubmitting = false,
  handleBtnClick,
  buttons = [],
}: CustomBtnGroupProps) => {
  const customButtons: ButtonConfig[] = buttons.map((button) => ({
    ...button,
    disabled: isSubmitting,
    handleBtnClick: button.handleBtnClick || handleBtnClick,
  }))
  return <CommonButtonGroup buttons={customButtons} />
}

/************************************************************************
  #### 이 름 : `SearchBtnGroupProps`
  #### 설  명 : 검색 버튼 그룹 Props를 정의한 인터페이스  
  #### 프로퍼티 :  

  > - searchProps(Partial<ButtonConfig>): 검색 버튼에 대한 추가 설정
  > - resetProps(Partial<ButtonConfig>): 초기화 버튼에 대한 추가 설정
 ************************************************************************/
interface SearchBtnGroupProps extends BaseBtnGroupProps {
  searchProps?: Partial<ButtonConfig>
  resetProps?: Partial<ButtonConfig>
}

/************************************************************************
  #### 이 름 : `SearchBtnGroup`
  #### 설  명 : 검색 및 초기화 버튼을 포함하는 버튼 그룹 컴포넌트
  #### 인  자 :  

  > - isSubmitting(boolean): 버튼의 비활성화 여부
  > - handleBtnClick(function): 공통 버튼 클릭 이벤트 핸들러
  > - searchProps(object): 검색 버튼에 대한 추가 설정. 없으면 기본값 사용
  > - resetProps(object): 초기화 버튼에 대한 추가 설정. 없으면 기본값 사용

  #### 사용법 :  

  ```tsx
  <SearchBtnGroup
      isSubmitting={isSubmitting}
      handleBtnClick={handleBtnClick}
      searchProps={{
        customText: '검색하기',
        type: 'submit',
      }}
      resetProps={{
        customText: '초기화',
        isVisible: true,
      }}
  />
  ```
 ************************************************************************/
export const SearchBtnGroup = ({
  isSubmitting = false,
  handleBtnClick,
  searchProps = {},
  resetProps = {},
  buttons = [],
}: SearchBtnGroupProps) => {
  const defaultButtons: ButtonConfig[] = [
    {
      name: 'search',
      actionType: 'search',
      disabled: isSubmitting,
      handleBtnClick: handleBtnClick,
      ...searchProps,
    },
    {
      name: 'reset',
      actionType: 'reset',
      disabled: isSubmitting,
      handleBtnClick: handleBtnClick,
      ...resetProps,
    },
  ]

  const allButtons = [...buttons, ...defaultButtons]

  return (
    <div className="search-bottom-control">
      <CommonButtonGroup buttons={allButtons} />
    </div>
  )
}

/************************************************************************
  #### 이 름 : `CreateBtnGroupProps`
  #### 설  명 : 등록 버튼 그룹 Props를 정의한 인터페이스  
  #### 프로퍼티 :  

  > - saveProps(Partial<ButtonConfig>): 임시 버튼에 대한 추가 설정
  > - submitProps(Partial<ButtonConfig>): 제출 버튼에 대한 추가 설정
  > - cancelProps(Partial<ButtonConfig>): 취소 버튼에 대한 추가 설정
 ************************************************************************/
interface CreateBtnGroupProps extends BaseBtnGroupProps {
  saveProps?: Partial<ButtonConfig>
  submitProps?: Partial<ButtonConfig>
  cancelProps?: Partial<ButtonConfig>
}

/************************************************************************
  #### 이 름 : `CreateBtnGroup`
  #### 설  명 : 등록 화면의 관련 버튼을 포함하는 버튼 그룹 컴포넌트
  #### 인  자 :  

  > - isSubmitting(boolean): 버튼의 비활성화 여부
  > - handleBtnClick(function): 공통 버튼 클릭 이벤트 핸들러
  > - saveProps(object): 저장 버튼에 대한 추가 설정. 없으면 기본값 사용
  > - submitProps(object): 제출 버튼에 대한 추가 설정. 없으면 기본값 사용
  > - cancelProps(object): 취소 버튼에 대한 추가 설정. 없으면 기본값 사용

  #### 사용법 :  

  ```tsx
  <CreateBtnGroup
      isSubmitting={isSubmitting}
      handleBtnClick={handleBtnClick}
      saveProps={{
        customText: '임시저장',
        isVisible: false,
      }}
      submitProps={{
        customText: '등록하기',
      }}
      cancelProps={{
        customText: '취소',
        handleBtnClick: handleCancel,
      }}
  />
  ```
 ************************************************************************/
export const CreateBtnGroup = ({
  isSubmitting = false,
  handleBtnClick,
  saveProps = {},
  submitProps = {},
  cancelProps = {},
  buttons = [],
}: CreateBtnGroupProps) => {
  const defaultButtons: ButtonConfig[] = [
    {
      name: 'save',
      actionType: 'save',
      disabled: isSubmitting,
      handleBtnClick: handleBtnClick,
      ...saveProps,
    },
    {
      name: 'submit',
      actionType: 'submit',
      disabled: isSubmitting,
      handleBtnClick: handleBtnClick,
      ...submitProps,
    },
    {
      name: 'cancel',
      actionType: 'cancel',
      disabled: isSubmitting,
      handleBtnClick: handleBtnClick,
      ...cancelProps,
    },
  ]

  const allButtons = [...buttons, ...defaultButtons]

  return (
    <div className="table-bottom-control">
      <CommonButtonGroup buttons={allButtons} />
    </div>
  )
}

/************************************************************************
  #### 이 름 : `UpdateBtnGroupProps`
  #### 설  명 : 수정 버튼 그룹 Props를 정의한 인터페이스  
  #### 프로퍼티 :  

  > - deleteProps(Partial<ButtonConfig>): 삭제 버튼에 대한 추가 설정
  > - saveProps(Partial<ButtonConfig>): 저장 버튼에 대한 추가 설정
  > - submitProps(Partial<ButtonConfig>): 제출 버튼에 대한 추가 설정
  > - cancelProps(Partial<ButtonConfig>): 취소 버튼에 대한 추가 설정
 ************************************************************************/
interface UpdateBtnGroupProps extends BaseBtnGroupProps {
  deleteProps?: Partial<ButtonConfig>
  saveProps?: Partial<ButtonConfig>
  submitProps?: Partial<ButtonConfig>
  cancelProps?: Partial<ButtonConfig>
}

/************************************************************************
  #### 이 름 : `UpdateBtnGroup`
  #### 설  명 : 수정 화면 관련 버튼을 포함하는 버튼 그룹 컴포넌트
  #### 인  자 :  

  > - isSubmitting(boolean): 버튼의 비활성화 여부
  > - handleBtnClick(function): 공통 버튼 클릭 이벤트 핸들러
  > - deleteProps(object): 삭제 버튼에 대한 추가 설정. 없으면 기본값 사용
  > - saveProps(object): 저장 버튼에 대한 추가 설정. 없으면 기본값 사용
  > - submitProps(object): 제출 버튼에 대한 추가 설정. 없으면 기본값 사용
  > - cancelProps(object): 취소 버튼에 대한 추가 설정. 없으면 기본값 사용

  #### 사용법 :  

  ```tsx
  <UpdateBtnGroup
      isSubmitting={isSubmitting}
      handleBtnClick={handleBtnClick}
      deleteProps={{
        customText: '삭제하기',
        isVisible: true,
      }}
      saveProps={{
        customText: '임시저장',
        isVisible: false,
      }}
      submitProps={{
        customText: '수정완료',
      }}
      cancelProps={{
        customText: '취소',
        handleBtnClick: handleCancel,
      }}
  />
  ```
 ************************************************************************/
export const UpdateBtnGroup = ({
  isSubmitting = false,
  handleBtnClick,
  deleteProps = {},
  saveProps = {},
  submitProps = {},
  cancelProps = {},
  buttons = [],
}: UpdateBtnGroupProps) => {
  const defaultButtons: ButtonConfig[] = [
    {
      name: 'delete',
      actionType: 'delete',
      disabled: isSubmitting,
      handleBtnClick: handleBtnClick,
      ...deleteProps,
    },
    {
      name: 'save',
      actionType: 'save',
      disabled: isSubmitting,
      handleBtnClick: handleBtnClick,
      ...saveProps,
    },
    {
      name: 'submit',
      actionType: 'submit',
      disabled: isSubmitting,
      handleBtnClick: handleBtnClick,
      ...submitProps,
    },
    {
      name: 'cancel',
      actionType: 'cancel',
      disabled: isSubmitting,
      handleBtnClick: handleBtnClick,
      ...cancelProps,
    },
  ]

  const allButtons = [...buttons, ...defaultButtons]

  return (
    <div className="table-bottom-control">
      <CommonButtonGroup buttons={allButtons} />
    </div>
  )
}

/************************************************************************
  #### 이 름 : `ViewBtnGroupProps`
  #### 설  명 : 상세조회 버튼 그룹 Props를 정의한 인터페이스  
  #### 프로퍼티 :  

  > - recallProps(Partial<ButtonConfig>): 회수 버튼에 대한 추가 설정
  > - updateProps(Partial<ButtonConfig>): 수정 버튼에 대한 추가 설정
  > - cancelProps(Partial<ButtonConfig>): 취소 버튼에 대한 추가 설정
 ************************************************************************/
interface ViewBtnGroupProps extends BaseBtnGroupProps {
  deleteProps?: Partial<ButtonConfig>
  recallProps?: Partial<ButtonConfig>
  updateProps?: Partial<ButtonConfig>
  cancelProps?: Partial<ButtonConfig>
}

/************************************************************************
  #### 이 름 : `ViewBtnGroup`
  #### 설  명 : 상세조회 관련 버튼을 포함하는 버튼 그룹 컴포넌트
  #### 인  자 :  

  > - isSubmitting(boolean): 버튼의 비활성화 여부
  > - handleBtnClick(function): 공통 버튼 클릭 이벤트 핸들러
  > - recallProps(object): 회수 버튼에 대한 추가 설정. 없으면 기본값 사용
  > - updateProps(object): 수정 버튼에 대한 추가 설정. 없으면 기본값 사용
  > - cancelProps(object): 취소 버튼에 대한 추가 설정. 없으면 기본값 사용

  #### 사용법 :  

  ```tsx
  <ViewBtnGroup
      isSubmitting={isSubmitting}
      handleBtnClick={handleBtnClick}
      recallProps={{
        customText: '회수하기',
        isVisible: true,
      }}
      updateProps={{
        customText: '수정하기',
        isVisible: false,
      }}
      cancelProps={{
        customText: '목록으로',
        handleBtnClick: handleGoToList,
      }}
  />
  ```
 ************************************************************************/
export const ViewBtnGroup = ({
  isSubmitting = false,
  handleBtnClick,
  deleteProps = {},
  recallProps = {},
  updateProps = {},
  cancelProps = {},
  buttons = [],
}: ViewBtnGroupProps) => {
  const defaultButtons: ButtonConfig[] = [
    {
      name: 'delete',
      actionType: 'delete',
      disabled: isSubmitting,
      handleBtnClick: handleBtnClick || cancelProps.handleBtnClick,
      customText: '삭제',
      isVisible: false,
      ...deleteProps,
    },
    {
      name: 'recall',
      actionType: 'recall',
      disabled: isSubmitting,
      handleBtnClick: handleBtnClick,
      ...recallProps,
    },
    {
      name: 'update',
      actionType: 'update',
      disabled: isSubmitting,
      handleBtnClick: handleBtnClick,
      ...updateProps,
    },
    {
      name: 'cancel',
      actionType: 'cancel',
      disabled: isSubmitting,
      handleBtnClick: handleBtnClick,
      customText: '목록',
      ...cancelProps,
    },
  ]

  const allButtons = [...buttons, ...defaultButtons]

  return (
    <div className="table-bottom-control">
      <CommonButtonGroup buttons={allButtons} />
    </div>
  )
}
